import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  Provider,
  SignInWithPasswordCredentials,
  SupabaseClient,
} from '@supabase/supabase-js';
import { Database } from 'supabase/types/supabase';
import {
  getRoutePath,
  loginRoutePath,
  resetPasswordRoutePath,
  searchRoutePath,
} from '../../app.routes';
import { AlertService } from '../alert/alert.service';
import { AppleAuthService } from '../capacitor/apple-auth.service';
import { CapacitorService } from '../capacitor/capacitor.service';
import { GoogleAuthService } from '../capacitor/google-auth.service';
import { LoadingService } from '../loading/loading.service';
import { WindowService } from '../window/window.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private alertService = inject(AlertService);
  private appleAuthService = inject(AppleAuthService);
  private capacitorService = inject(CapacitorService);
  private loadingService = inject(LoadingService);
  private googleAuthService = inject(GoogleAuthService);
  private router = inject(Router);
  private supabaseClient = inject<SupabaseClient<Database>>(SupabaseClient);
  private windowService = inject(WindowService);

  async signInWithApple() {
    try {
      if (this.capacitorService.web) {
        await this.signInWithOAuth('apple');
      } else if (this.capacitorService.ios) {
        await this.signInWithAppleIOS();
        await this.router.navigate([getRoutePath(searchRoutePath)], {
          replaceUrl: true,
        });
      } else {
        throw new Error(
          'Sign in with Apple is only available on iOS and web browsers.'
        );
      }
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    }
  }

  async signInWithGoogle() {
    try {
      if (this.capacitorService.mobile) {
        await this.signInWithGoogleNative();
        await this.router.navigate([getRoutePath(searchRoutePath)], {
          replaceUrl: true,
        });
      } else {
        await this.signInWithOAuth('google');
      }
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    }
  }

  async signIn(credentials: SignInWithPasswordCredentials) {
    try {
      this.loadingService.setLoading(true);

      const { error } = await this.supabaseClient.auth.signInWithPassword(
        credentials
      );

      if (error) {
        throw error;
      }

      await this.router.navigate([getRoutePath(searchRoutePath)], {
        replaceUrl: true,
      });
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  signOut() {
    return this.supabaseClient.auth.signOut();
  }

  async deleteAccount() {
    try {
      this.loadingService.setLoading(true);

      const { error } = await this.supabaseClient.rpc('deleteUser');

      if (error) {
        throw error;
      }

      this.loadingService.setLoading(false);

      await this.router.navigate([getRoutePath(loginRoutePath)], {
        replaceUrl: true,
      });

      await this.alertService.createNotice({
        message: 'Account deleted successfully',
        duration: 5000,
        position: 'bottom',
      });
    } catch (error: unknown) {
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  async signUp(credentials: SignInWithPasswordCredentials) {
    try {
      const { error } = await this.supabaseClient.auth.signUp(credentials);

      if (error) {
        throw error;
      }

      await this.router.navigate([getRoutePath(searchRoutePath)], {
        replaceUrl: true,
      });
    } catch (error: unknown) {
      // Footer anchor not available before login
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    }
  }

  async forgotPassword(email: string) {
    try {
      this.loadingService.setLoading(true);

      const window = this.windowService.getWindow();
      const { error } = await this.supabaseClient.auth.resetPasswordForEmail(
        email,
        {
          redirectTo: `${window.location.origin}/${resetPasswordRoutePath}`,
        }
      );

      if (error) {
        throw error;
      }

      await this.alertService.createNotice({
        message: 'Password reset email sent! Please check your inbox.',
        duration: 5000,
        position: 'bottom',
      });
    } catch (error: unknown) {
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  async getSession() {
    return this.supabaseClient.auth.getSession();
  }

  async updatePassword(password: string) {
    try {
      this.loadingService.setLoading(true);

      const { error } = await this.supabaseClient.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      await this.alertService.createNotice({
        message: 'Password updated successfully!',
        duration: 5000,
        position: 'bottom',
      });

      // Redirect to search page after successful password update
      await this.router.navigate([getRoutePath(searchRoutePath)], {
        replaceUrl: true,
      });
    } catch (error: unknown) {
      const message = (error as Error).message || 'An error occurred';
      await this.alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  private async signInWithOAuth(provider: Provider) {
    const window = this.windowService.getWindow();
    const redirectTo = `${window.location.origin}/search`;
    return this.supabaseClient.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
      },
    });
  }

  private async signInWithAppleIOS() {
    const appleResponse = await this.appleAuthService.signInWithApple();
    const provider = 'apple';
    const token = appleResponse.result.idToken;

    if (!token) {
      throw new Error('Missing Apple token');
    }

    const response = await this.supabaseClient.auth.signInWithIdToken({
      provider,
      token,
    });

    const { error } = response;

    if (error) {
      throw error;
    }
  }

  private async signInWithGoogleNative() {
    const user = await this.googleAuthService.signInWithGoogle();
    const provider = 'google';

    if (user.result.responseType === 'offline') {
      throw new Error('Google offline login not supported');
    }

    const token = user.result.idToken;
    const access_token = user.result.accessToken?.token;

    if (!token || !access_token) {
      throw new Error('Missing Google token or access token');
    }

    const response = await this.supabaseClient.auth.signInWithIdToken({
      provider,
      token,
      access_token,
    });

    const { error } = response;

    if (error) {
      throw error;
    }

    return response;
  }
}
